<template>
  <auth-layout container-class="xl:max-w-2xl">
    <template #panel>
      <div></div>
    </template>
    <div
      v-if="success"
      class="text-center my-12 border border-positive-500 bg-positive-100 p-6 rounded text-positive-800"
    >
      <h1 class="text-2xl font-semibold mb-4">Registration Successful</h1>
      <p>Thank you for applying to be an assessor. We will be in touch soon.</p>
      <p>
        <icon-link :href="route('login')">
          Return to Login
        </icon-link>
      </p>
    </div>
    <div v-else>
      <div class="text-left content" v-html="parseMarkdown(content)"></div>
      <form
        v-if="!success"
        enctype="multipart/form-data"
        novalidate
        class="grid w-full max-w-lg mx-auto"
        @submit.prevent="submitForm"
      >
        <div class="pt-2 pb-6 col-span-full">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Your Application for {{ $config.name }} {{ $config.current_wave }}</span>
            </p>
          </div>
        </div>
        <auth-input
          v-model="form.name"
          label="Name"
          input-type="text"
          :error="getError('name')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          class="col-span-3"
        />

        <auth-input
          v-model="form.email"
          label="Email"
          input-type="email"
          :error="getError('email')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          placeholder="e.g. your.name@domain.com"
          class="col-span-2"
        />

        <auth-input
          v-model="form.telephone"
          label="Telephone"
          input-type="tel"
          :error="getError('telephone')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          placeholder="e.g. +44(0) 1234 567 890"
          class="col-span-2"
        />

        <auth-input
          v-model="form.linkedin"
          label="LinkedIn Profile"
          input-type="url"
          :error="getError('linkedin')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          placeholder="e.g. https://www.linkedin.com/in/yourprofile"
          class="col-span-3"
        />

        <auth-input
          v-model="form.company_name"
          label="Employer / Company Name"
          input-type="text"
          :error="getError('company_name')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          class="col-span-3"
        />

        <auth-input
          v-model="form.company_role"
          label="Your Role / Job Title"
          input-type="text"
          :error="getError('company_role')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          class="col-span-2"
        />

        <auth-textarea
          v-model="form.address"
          label="Company Address"
          :error="getError('address')"
          :required="true"
          :rows="5"
          :loading="loading"
          class="col-span-full"
        />

        <div class="pt-2 pb-6 col-span-full">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Experience</span>
            </p>
          </div>
        </div>

        <auth-textarea
          v-model="form.appointments"
          label="Appointments, directorships, and consultancies"
          :error="getError('appointments')"
          :required="true"
          :rows="5"
          :loading="loading"
          class="col-span-full"
        >
          <template #instructions>
            Please list any current or past appointments, directorships, and consultancies you hold or have held in the last 5 years.
          </template>
        </auth-textarea>

        <auth-textarea
          v-model="form.skills_experience_ze"
          label="Please outline your skills and experience with zero-emission vehicle technology within the last 5 years."
          :error="getError('skills_experience_ze')"
          :required="true"
          :loading="loading"
          :rows="5"
          :maxlength="500"
          class="col-span-full"
        />

        <auth-textarea
          v-model="form.skills_experience_cam"
          label="Please outline your skills and experience with CAM technology within the last 5 years."
          :error="getError('skills_experience_cam')"
          :required="true"
          :loading="loading"
          :rows="5"
          :maxlength="500"
          class="col-span-full"
        />

        <auth-textarea
          v-model="form.past_experience"
          label="Please outline any previous assessing experience."
          :error="getError('past_experience')"
          :required="true"
          :loading="loading"
          :rows="5"
          :maxlength="500"
          class="col-span-full"
        />

        <div class="col-span-full text-left">
          <auth-input
            id="cv_upload"
            v-model="form.cv_upload"
            label="Upload your CV"
            input-type="file"
            :error="getError('cv_upload')"
            :required="true"
            :loading="loading"
            class="col-span-full"
          >
            <p class="text-sm italic mt-1">
              Please provide your CV in PDF. Max file size is 2mb
            </p>
          </auth-input>
          <hr class="border-b-2 my-12">
          <label class="form-label">Please confirm that you are available for both the written assessment and interviews.</label>
          <input-checkbox
            v-model="form.confirmation"
            :value="true"
            :error="getError('confirmation')"
            required
            class="mt-6"
            label-class="text-base"
          >
            I confirm my availability for both the written assessment (13th Feb - 2nd Mar 2026) and interviews (18th Mar - 27th Mar 2026).
          </input-checkbox>
        </div>

        <app-button
          class="col-span-2 my-8"
          type="submit"
          :loading="loading"
          block
        >
          Submit Application
        </app-button>
      </form>
    </div>
  </auth-layout>
</template>

<script>
import { requestMixin } from '../mixins/request';

const storageKey = 'assessor_registration_form';
const defaults = {
  name: "",
  email: "",
  telephone: "",
  linkedin: "",
  company_name: "",
  company_role: "",
  address: "",
  appointments: "",
  skills_experience_ze: "",
  skills_experience_cam: "",
  past_experience: "",
  confirmation: false,
  cv_upload: null,
};
export default {
  name: 'AssessorRegistration',
  mixins: [requestMixin],
  data() {
    return {
      success: false,
      loading: false,
      form: { ...defaults },
    }
  },
  computed: {
    content() {
      return this.state?.content
    },
  },
  watch: {
    form: {
      deep: true,
      handler: function() {
        localStorage.setItem(storageKey, JSON.stringify({...this.form, cv_upload: null}));
      },
    },
  },
  created() {
    const savedForm = localStorage.getItem(storageKey);
    if (savedForm) {
      this.form = JSON.parse(savedForm);
    }
  },
  methods: {
    submitForm(event) {
      this.loading = true;
      const formData = new FormData();
      for (const key in this.form) {
        formData.append(key, this.form[key]);
      }

      if(event.target.cv_upload.files.length > 0) {
        formData.append('cv_upload', event.target.cv_upload.files[0]);
      }

      const params = new URLSearchParams(window.location.search);

      if(params.has('signature')) {
        formData.append('signature', params.get('signature'));
      }

      axios.post(window.location.pathname, formData)
        .then(() => {
          this.success = true;
          this.form = { ...defaults };
          localStorage.removeItem(storageKey);
          this.$el.parentElement.scrollTo({ top: 0, behavior: 'smooth' });
        })
        .catch(error => this.requestError(error))
        .then(() => {
          event.target.scrollIntoView({ behavior: 'smooth' });
          this.loading = false;
        });
    },
  },
}
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.col-span-full {
  grid-column: 1 / -1;
}

.col-span-3 {
  grid-column: span 3 / span 3;
}

.col-span-2 {
  grid-column: span 2 / span 2;
}
</style>

